import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaSyncAlt } from "react-icons/fa";
import Dropdown from "../../Component/Common/Dropdown";
import { InputField } from "../../Component/Common/InputField";
import CustomPagination from "../../Component/Common/CustomPagination";
import ConfirmModal from "../../Component/Common/ConstomModal/ConfirmModal";
import { handleKeyDown } from "../../helper";
import { VoterTable } from "./components/VoterTable";
import { useVoterListFetch } from "./components/useVoterListFetch";
import { PER_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from "./components/voterHelpers";

// Search-first page filtered/sorted by age. There is no age-range API param
// on the backend (confirmed during inspection), so this filters/sorts
// client-side over the same booth-scoped full fetch that already powers the
// stats bar (`statsList`) - the same pattern that fixed the 502-overload
// problem for Full Voter's other client-side filters, so a booth must be
// selected before searching (same requirement as the stats bar itself).
export const AgeWiseSearch = () => {
  const navigate = useNavigate();
  const v = useVoterListFetch();

  const [ageFrom, setAgeFrom] = useState("");
  const [ageTo, setAgeTo] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);

  const ageFilteredSorted = useMemo(() => {
    const source = Array.isArray(v.statsList?.results) ? v.statsList.results : [];
    const from = ageFrom === "" ? -Infinity : Number(ageFrom);
    const to = ageTo === "" ? Infinity : Number(ageTo);
    return source
      .filter((voter) => {
        const age = Number(voter.age);
        if (Number.isNaN(age)) return false;
        return age >= from && age <= to;
      })
      .sort((a, b) => Number(a.age) - Number(b.age));
  }, [v.statsList, ageFrom, ageTo]);

  const pageCount = Math.max(Math.ceil(ageFilteredSorted.length / limit), 0);
  const pageVoters = ageFilteredSorted.slice(page * limit, page * limit + limit);

  // Pass this page's path so the voter form closes back here (e.g. the
  // Dashboard), not always to Voter Search.
  const location = useLocation();
  const backState = { state: { from: location.pathname } };
  const editVoter = (item) => navigate(`/voter-management/add?editId=${item.id}`, backState);
  const viewVoter = (item) => navigate(`/voter-management/add?editId=${item.id}&view=1`, backState);

  return (
    <div className="voter-page-shell">
      <div className="tabMain_head voter-page-header">
        <div className="Tab_dropList">
          <h5 style={{ margin: 0 }}>Age Wise Search</h5>
          <div className="flex_assambly_drop">
            <div>
              <Dropdown
                className="dropdownoforder"
                placeholder="Select Assembly Number"
                options={v.assamblyList || []}
                valueText="value"
                labelText="label"
                isClearable
                selectedOption={v.formOrderName?.assemblyFilter}
                setSelectedOption={v.handleAssemblyChange}
              />
            </div>
            <div>
              <Dropdown
                className="dropdownoforder"
                placeholder="Select Booth Number"
                options={v.boothList || []}
                valueText="value"
                labelText="label"
                isClearable
                selectedOption={v.formOrderName?.boothFilter}
                setSelectedOption={v.handleBoothChange}
              />
            </div>
          </div>
        </div>
      </div>

      {hasSearched && v.filter?.booth_number && (
        <div className="voter-booth-banner">
          <div className="voter-booth-banner-info">
            <FaMapMarkerAlt className="voter-booth-banner-icon" />
            <span className="voter-booth-banner-label">Booth Address :</span>
            <span className="voter-booth-banner-address">{v.boothAddress || "-"}</span>
          </div>
        </div>
      )}

      <div className="anvTask_prog voter-filter-section">
        <div className="voter-filter-bar">
          <div className="voter-filter-item">
            <InputField
              label="Age From"
              labelClass="voter-filter-label"
              placeholder="Min Age"
              className="anvSearch_input voter-filter-input"
              boxClass="voter-filter-control"
              maxLength={3}
              onKeyDown={handleKeyDown}
              value={ageFrom}
              onChange={(e) => setAgeFrom(e.target.value)}
            />
          </div>
          <div className="voter-filter-item">
            <InputField
              label="Age To"
              labelClass="voter-filter-label"
              placeholder="Max Age"
              className="anvSearch_input voter-filter-input"
              boxClass="voter-filter-control"
              maxLength={3}
              onKeyDown={handleKeyDown}
              value={ageTo}
              onChange={(e) => setAgeTo(e.target.value)}
            />
          </div>
          <div className="voter-filter-item voter-filter-toggle-item">
            <button
              type="button"
              className="voter-filter-clear-btn"
              onClick={() => {
                setAgeFrom("");
                setAgeTo("");
                setHasSearched(false);
                setPage(0);
              }}
            >
              <FaSyncAlt />
              <span>Clear</span>
            </button>
          </div>
          <div className="voter-filter-item voter-filter-action">
            <button
              type="button"
              className="addPlan"
              onClick={() => {
                setPage(0);
                setHasSearched(true);
              }}
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {!hasSearched ? (
        <div className="voter-stats-empty" style={{ margin: 16 }}>
          Select a booth, optionally set an age range, and click Search.
        </div>
      ) : !v.filter?.booth_number ? (
        <div className="voter-stats-empty" style={{ margin: 16 }}>
          Select a booth first - age search works within one booth at a time.
        </div>
      ) : v.isStatsLoading ? (
        <div className="voter-stats-empty" style={{ margin: 16 }}>
          Loading…
        </div>
      ) : v.statsError ? (
        <div className="voter-stats-error" style={{ margin: 16 }}>
          <span>{v.statsError}</span>
          <button type="button" className="voter-stats-retry-btn" onClick={v.retryStats}>
            <FaSyncAlt />
            <span>Retry</span>
          </button>
        </div>
      ) : (
        <>
          <VoterTable
            voters={pageVoters}
            tableLoading={false}
            userLogin={v.userLogin}
            selectable={false}
            onView={viewVoter}
            onEdit={editVoter}
            onDelete={(item) => v.hendleDeleteTag(item, true)}
          />

          {ageFilteredSorted.length > 0 && (
            <div className="voter-pagination-bar">
              <div className="voter-pagination-info">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, ageFilteredSorted.length)} of{" "}
                {ageFilteredSorted.length} entries
              </div>
              <div className="voter-pagination-controls">
                <div className="voter-per-page">
                  <span>Per Page</span>
                  <Dropdown
                    className="voter-per-page-select"
                    classNamePrefix="voterFilterSelect"
                    options={PER_PAGE_OPTIONS}
                    valueText="value"
                    labelText="label"
                    selectedOption={PER_PAGE_OPTIONS.find((option) => option.value === limit)}
                    setSelectedOption={(data) => {
                      setLimit(data?.value || DEFAULT_PAGE_SIZE);
                      setPage(0);
                    }}
                  />
                </div>
                {pageCount > 1 && (
                  <CustomPagination
                    onPageChange={(data) => setPage(data.selected)}
                    currentPage={page}
                    pageCount={pageCount}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal isOpen={v.openDel} toggle={() => v.setOpenDel(!v.openDel)} onSubmit={v.handleDelete} />
    </div>
  );
};
